import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

export interface Tablemate {
  id: string;
  firstName: string;
  lastName?: string;
  profileImageUrl?: string | null;
}

export interface PostDinnerFeedbackSubmission {
  bookingId: string;
  overallExperience?: number;
  feltWelcomed?: string;
  restaurantSelection?: string;
  futureAttendance?: string;
  recommendationScore?: number;
  notes?: string;
}

export interface PostDinnerFeedbackData {
  overallExperience?: number | null;
  feltWelcomed?: string | null;
  restaurantSelection?: string | null;
  futureAttendance?: string | null;
  recommendationScore?: number | null;
  notes?: string | null;
  connections?: { toUserId: string; contactMethod: string }[];
}

/**
 * Hook to fetch tablemates for a specific booking
 */
export function useTablemates(bookingId: string | null) {
  return useQuery({
    queryKey: ['post-dinner', 'tablemates', bookingId],
    queryFn: async (): Promise<Tablemate[]> => {
      return (await api.get<Tablemate[]>(`/user/post-dinner/tablemates?bookingId=${bookingId}`)).data || [];
    },
    enabled: !!bookingId,
  });
}

/**
 * Hook to fetch existing feedback for a booking
 */
export function usePostDinnerFeedback(bookingId: string | null) {
  return useQuery({
    queryKey: ['post-dinner', 'feedback', bookingId],
    queryFn: async (): Promise<PostDinnerFeedbackData | null> => {
      if (!bookingId) return null;
      const response = await api.get<PostDinnerFeedbackData | null>(
        `/user/post-dinner/feedback?bookingId=${bookingId}`
      );
      return response.data || null;
    },
    enabled: !!bookingId,
  });
}

/**
 * Hook to submit post-dinner feedback
 */
export function useSubmitPostDinnerFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PostDinnerFeedbackSubmission) => {
      return api.post('/user/post-dinner/submit', data);
    },
    onSuccess: (_, data) => {
      // Invalidate completed feedback to refresh the Events page
      queryClient.invalidateQueries({ queryKey: ['post-dinner', 'completed'] });
      queryClient.invalidateQueries({ queryKey: ['post-dinner', 'feedback', data.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', data.bookingId] });
    },
  });
}

export function useNoShows(bookingId: string | null) {
  return useQuery({
    queryKey: ['post-dinner', 'no-shows', bookingId],
    queryFn: async (): Promise<string[]> => {
      return (await api.get<string[]>(`/user/post-dinner/no-shows?bookingId=${bookingId}`)).data || [];
    },
    enabled: !!bookingId,
  });
}

export function useReportNoShow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { bookingId: string; reportedUserId: string }) => {
      return api.post<{ reported: boolean }>('/user/post-dinner/no-show', data);
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ['post-dinner', 'no-shows', data.bookingId] });
    },
  });
}

/**
 * Hook to update connections for a booking
 */
export function useUpdateConnections() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { bookingId?: string; connections: Array<{ toUserId: string; contactMethods: string[] }> }) => {
      return api.post('/user/post-dinner/connections', data);
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      if (data.bookingId) {
        queryClient.invalidateQueries({ queryKey: ['post-dinner', 'feedback', data.bookingId] });
      }
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

